/**
 * Procedural level generation.
 *
 * One entry point, one generator profile per `LevelSpec.generator`. A profile
 * only carves cells and appends regions; everything that has to be true of
 * *every* level — that the whole map is reachable, that walls nothing can see
 * are cheap, that placement is fair — is enforced here, once, for all of them.
 *
 * The same seed always produces the same level: every decision routes through
 * the seeded RNG, and no pass depends on iteration order of anything unordered.
 */

import { makePropIndex } from '../core/props';
import { makeRNG } from '../core/rng';
import type { GeneratedLevel, LevelSpec } from '../core/types';
import { repairConnectivity } from './gen/connect';
import { generateEndlessCity } from './gen/endlesscity';
import { dressFunrooms, generateFunrooms } from './gen/funrooms';
import { generateHotel } from './gen/hotel';
import { generateHub } from './gen/hub';
import { generateLightsOut } from './gen/lightsout';
import { generateLobby } from './gen/lobby';
import { dressMotion, generateMotion } from './gen/motion';
import { generateOffices } from './gen/offices';
import { generatePipes } from './gen/pipes';
import { populate } from './gen/placement';
import { placeProps } from './gen/props';
import { generatePoolrooms } from './gen/poolrooms';
import { makeSite, shellify, snapRegionCenters } from './gen/site';
import { generateStation } from './gen/station';
import { installSlides, signpostExits } from './gen/slides';
import { generateWarehouse } from './gen/warehouse';

/**
 * Build a playable level from an authored spec. Deterministic in `seed`:
 * generating twice with the same arguments gives identical grids, items,
 * lights and spawns.
 */
export function generateLevel(spec: LevelSpec, seed: number): GeneratedLevel {
  const rng = makeRNG(seed);
  const site = makeSite(spec, rng);

  switch (spec.generator) {
    case 'lobby':
      generateLobby(site);
      break;
    case 'warehouse':
      generateWarehouse(site);
      break;
    case 'pipes':
      generatePipes(site);
      break;
    case 'poolrooms':
      generatePoolrooms(site);
      break;
    case 'hub':
      generateHub(site);
      break;
    case 'funrooms':
      generateFunrooms(site);
      break;
    case 'motion':
      generateMotion(site);
      break;
    case 'offices':
      generateOffices(site);
      break;
    case 'station':
      generateStation(site);
      break;
    case 'hotel':
      generateHotel(site);
      break;
    case 'lightsout':
      generateLightsOut(site);
      break;
    case 'city':
      generateEndlessCity(site);
      break;
  }

  repairConnectivity(site.grid);
  shellify(site.grid);
  snapRegionCenters(site);

  const placed = populate(site);

  // Scenery goes last: it reads the finished grid and the spawn distances so it
  // can hug walls that will still be there and thin out near the spawn.
  const props = placeProps({
    spec,
    grid: site.grid,
    regions: site.regions,
    rng: rng.fork(0x9707),
    distanceFromSpawn: placed.distanceFromSpawn,
    exits: placed.exits,
    // Handed over so the placer can keep solid props off the supply spots. A
    // bathtub standing on a granola bar is the one collision outcome the player
    // is entitled to be annoyed by.
    items: placed.items,
  });

  // Motion is a neighbourhood rather than a building, and a suburb is a set of
  // rules about open ground that the general placer has no vocabulary for: which
  // strip of lawn is a street, which side of a house the door is on, where a
  // plot ends. So it dresses itself, after the general pass so it can see and
  // avoid everything already standing, and before the slides so the way down is
  // still installed over the top of the finished scenery.
  if (spec.generator === 'motion') dressMotion(site, rng.fork(0x94a1), props, placed.exits);

  // The Funrooms dresses itself for the same reason, from the other end: there
  // the general placer has no vocabulary for a soft-play *fit-out*. It draws
  // pockets of scenery, and a soft-play centre is not a hall with equipment in
  // it — the equipment is the hall, laid out as zones with a climbing frame
  // spanning one and a ball pool covering the floor of the next. Same seam and
  // the same reasons: after the general pass so it can avoid what is already
  // standing, before the slides so the way down goes in over the top.
  if (spec.generator === 'funrooms') {
    // The spawn goes with the exits and the supplies. It is the one point on
    // the level the player is guaranteed to occupy, and a pass that stands a
    // padded column on one cell in three will eventually stand one on it.
    dressFunrooms(site, rng.fork(0xfa11), props, placed.exits, placed.items, placed.spawn);
  }

  // The way down goes in last, over the finished scenery. Both passes need to
  // see what the landmark pass already hung on the walls, and the slide itself
  // has to be the object standing where the exit is — a mouth the player can
  // see from across the room and the point they trigger have to be the same
  // thing. Ids are renumbered afterwards, following the same convention as
  // `placeProps`: no pass has to agree with any other one about a counter.
  const dress = {
    spec,
    grid: site.grid,
    rng: rng.fork(0x511d),
    props,
    exits: placed.exits,
    slides: placed.slides,
  };
  installSlides(dress);
  signpostExits(dress);
  for (let i = 0; i < props.length; i++) props[i]!.id = i;

  return {
    spec,
    grid: site.grid,
    regions: site.regions,
    spawn: placed.spawn,
    spawnYaw: placed.spawnYaw,
    exits: placed.exits,
    lights: placed.lights,
    items: placed.items,
    entitySpawns: placed.entitySpawns,
    props,
    propIndex: makePropIndex(props, site.grid.cellSize),
    seed,
    distanceFromSpawn: placed.distanceFromSpawn,
  };
}
